<script setup lang="ts">
import WaveDividerV2 from '~/components/atoms/WaveDividerV2/WaveDividerV2.vue'
import WaveDivider from '~/components/atoms/WaveDivider/WaveDivider.vue'
import GridImageCards from '~/components/organisms/GridImageCards/GridImageCards.vue'
import EconomiaSectionClub from '~/components/organisms/EconomiaSectionClub/EconomiaSectionClub.vue'
import ExploreSectionClub from '~/components/organisms/ExploreSectionClub/ExploreSectionClub.vue'
import FeriasSection from '~/components/organisms/FeriasSection/FeriasSection.vue'
import InvestmentSectionClub from '~/components/organisms/InvestmentSectionClub/InvestmentSectionClub.vue'
import MoreInfoSection from '~/components/organisms/MoreInfoSection/MoreInfoSection.vue'
import { fetchLoyaltyClubPage } from '~/composables/useLoyalty/useLoyalty'
import type { LoyaltyClubPage } from '~/composables/useLoyalty/useLoyalty.types'
import { useSeoHead } from '~/composables/useSeo/useSeoHead'
import NewsletterFormModal from '~/components/organisms/NewsletterFormModal/NewsletterFormModal.vue'
import ContactFormModal from '~/components/organisms/ContactFormModal/ContactFormModal.vue'
import type { ContactFormData, NewsletterFormData } from '~/types/forms'
import CarouselCardHero from '~/components/molecules/CarouselCardHero/CarouselCardHero.vue'
import { useLayoutConfigStore } from '~/stores/useLayoutConfigStore'
import type { SchedulerBanner } from '~/types/scheduler-banner'

const { locale, t } = useI18n()
const localePath = useLocalePath()
const layoutConfigStore = useLayoutConfigStore()
const contactModal = useContactModal()
const newsletterModal = useNewsletterModal()

const loyaltyData = (await fetchLoyaltyClubPage(locale.value)) as LoyaltyClubPage

const pageData = computed(() => loyaltyData)
const mainBannerData = computed(() => {
  const banners = loyaltyData.mainBanner || []
  return banners.map((banner) => {
    if (banner?.cta?.target !== 'action') {
      return banner
    }

    return {
      ...banner,
      cta: {
        ...banner.cta,
        onClick: (event: MouseEvent) => {
          event.preventDefault()
          openNewsletterModal('Fidelidade Clube')
        }
      }
    } as SchedulerBanner
  })
})
const cansadoData = computed(() => loyaltyData.cansadoSection)
const feriasData = computed(() => loyaltyData.feriasSection)
const exploreData = computed(() => loyaltyData.exploreSection)
const economiaData = computed(() => loyaltyData.economiaSection)
const investimentData = computed(() => loyaltyData.investmentSection)
const moreInfoData = computed(() => loyaltyData.moreInfoSection)

useSeoHead({
  seo: computed(() => pageData.value?.seo ?? null),
  locale,
  titleFallback: computed(() => pageData.value?.title ?? ''),
  descriptionFallback: computed(() => pageData.value?.description ?? ''),
  addXDefaultAlternate: true
})

watchEffect(() => {
  if (!pageData.value) return
  layoutConfigStore.breadcrumbs = [
    { label: 'Home', to: localePath('/') },
    { label: pageData.value?.seo?.breadcrumbTitle ?? '' }
  ]
  useRecentlyViewedStore().add(
    pageData.value?.slug,
    pageData.value?.seo?.breadcrumbTitle,
    pageData.value?.seo?.image?.url
  )
})

const lastSubmission = ref<ContactFormData | NewsletterFormData | null>()

function openNewsletterModal(origin: string) {
  newsletterModal.open(origin)
}

function openContactModal(origin: string) {
  contactModal.open(origin)
}

const handleSubmit = (data: ContactFormData | NewsletterFormData) => {
  lastSubmission.value = data
}

onMounted(() => {
  layoutConfigStore.updateBottomNavPrimaryCta({
    kind: 'lead',
    contactSource: 'Fidelidade Clube - bottom nav'
  })
})

onUnmounted(() => {
  layoutConfigStore.updateBottomNavPrimaryCta(null)
})
</script>

<template>
  <div class="theme-fidelidade-club">
    <div v-if="mainBannerData?.length > 0" class="relative w-full h-screen overflow-hidden">
      <div class="absolute w-full h-full md:flex overflow-hidden pointer-events-none">
        <div class="container relative z-2 h-full">
          <div
            class="wave-blue absolute -scale-x-100 w-[30%] top-[70%] md:top-1/2 -right-[18px] md:right-auto md:-left-10 md:scale-x-100 md:w-[300px]"
          >
            <NuxtImg
              src="/images/loyalty-hero-element-blue.svg"
              alt="Decoration Blue"
              class="w-[300px]"
              loading="lazy"
            />
          </div>
          <div
            class="wave-orange absolute w-[30%] bottom-0 left-0 right-auto -translate-y-1/2 md:w-auto md:bottom-1/2 md:left-auto md:right-8 md:translate-y-0 md:scale-x-[-1]"
          >
            <NuxtImg
              src="/images/loyalty-hero-element-orange.svg"
              alt="Decoration Blue"
              class="w-[320px]"
              loading="lazy"
            />
          </div>
          <div
            class="flex seal absolute bottom-0 right-0 flex items-center justify-center h-full w-full max-w-[80px] max-h-[72px] mb-6 mr-6 md:max-w-[143px] md:max-h-[126px] md:mb-12 md:mr-14"
          >
            <NuxtImg
              src="/images/fidelidade/logo-clube_ferias.svg"
              alt="Logo Clube de Férias"
              class="w-[143px]"
              width="143"
              loading="lazy"
            />
          </div>
        </div>
      </div>
      <CarouselCardHero
        id="mainBanner"
        :slides="mainBannerData"
        desktop-placement="bottom-start"
        height="100vh"
        :content-padding-top="80"
        :responsive="{
          md: { contentPaddingTop: 0 },
          lg: { contentPaddingTop: 0 }
        }"
        :content-root-cardhero="{
          contentRoot: 'px-6 lg:px-10',
          textButtonWrapper: 'pt-6 md:pt-22',
          button: 'px-3! typo-button-secondary! leading-4! min-w-[249px]'
        }"
        :preload-first-slide="true"
      />
    </div>
  </div>

  <GridImageCards
    v-if="cansadoData"
    :class="!mainBannerData?.length && 'pt-35!'"
    :title="cansadoData.title"
    :sub-title="cansadoData.subTitle"
    :description="cansadoData.description"
    :image="cansadoData.image"
    :items="cansadoData.items"
  />

  <WaveDivider
    :curvature-boost="5"
    bottom-color="var(--color-foundation-laranja)"
    top-color="transparent"
  ></WaveDivider>

  <FeriasSection
    v-if="feriasData"
    :title="feriasData.title"
    :items="feriasData.items"
    :button="{
      onClick: () => openNewsletterModal('Fidelidade Clube'),
      label: feriasData.cta?.label ?? '',
      variant: 'solid',
      appearance: 'white',
      trailingIcon: ''
    }"
  />

  <WaveDivider
    :curvature-boost="5"
    bottom-color="transparent"
    top-color="var(--color-foundation-laranja)"
  ></WaveDivider>

  <div v-if="exploreData" class="container relative 1">
    <ExploreSectionClub
      :title="exploreData.title"
      :sub-title="exploreData.subTitle"
      :items="exploreData.items"
      :button="{
        onClick: () => openNewsletterModal('Fidelidade Clube'),
        label: exploreData.cta?.label ?? '',
        variant: 'solid',
        trailingIcon: ''
      }"
    />
  </div>

  <div v-if="economiaData" class="bg-neutral-5">
    <div class="container relative">
      <EconomiaSectionClub
        :title="economiaData.title"
        :sub-title="economiaData.subTitle"
        :image="economiaData.image"
        :items="economiaData.items"
        :button="{
          onClick: () => openNewsletterModal('Fidelidade Clube'),
          label: economiaData.cta?.label ?? '',
          variant: 'solid',
          trailingIcon: ''
        }"
      />
    </div>
  </div>

  <WaveDivider :curvature-boost="5" bottom-color="var(--color-foundation-azul2)" top-color="var(--color-neutral-5)" />

  <div v-if="investimentData" class="bg-foundation-azul2">
    <div class="container relative">
      <InvestmentSectionClub
        :title="investimentData.title"
        :sub-title="investimentData.subTitle"
        :items="investimentData.items"
        :button="{
          onClick: () => openNewsletterModal('Fidelidade Clube'),
          label: investimentData.cta?.label ?? '',
          variant: 'solid',
          trailingIcon: ''
        }"
      />
    </div>
  </div>

  <WaveDividerV2
    :curvature-boost="5"
    bottom-color="var(--color-neutral-5)"
    bottom-color-right="var(--color-foundation-verde3)"
    top-color="var(--color-foundation-azul2)"
    class="hidden md:block"
  />

  <WaveDivider
    :curvature-boost="5"
    bottom-color="var(--color-neutral-5)"
    top-color="var(--color-foundation-azul2)"
    class="md:hidden"
  />

  <MoreInfoSection
    v-if="moreInfoData"
    :faq="moreInfoData.faq"
    :banner="moreInfoData.banner"
    :button="{
      onClick: () => openContactModal('Fidelidade Clube'),
      label: moreInfoData.banner?.cta?.label ?? '',
      variant: 'solid',
      trailingIcon: ''
    }"
  />
  <NewsletterFormModal
    :is-open="newsletterModal.isOpen.value"
    :source="newsletterModal.source.value"
    @update:is-open="newsletterModal.isOpen.value = $event"
    @submit="handleSubmit"
  />
  <ContactFormModal
    :is-open="contactModal.isOpen.value"
    :source="contactModal.source.value"
    @update:is-open="contactModal.isOpen.value = $event"
    @submit="handleSubmit"
  />
</template>
